// Finance agent API helper — all endpoints grouped under /api/finance/agents/<feature>/*.
// Advanced v2: supports use_ai toggle, composite scans, AI narrative endpoints.
import axios from '../../../utils/axiosInstance';

const BASE = '/api/finance/agents';
const RPA = '/api/finance/rpa';

// =======================================================================
// Transaction Reconciliation (Merchant RPA)
// =======================================================================

// 28.1 Merchants
export const rpaMerchantList = (params) =>
    axios.get(`${RPA}/merchants`, { params });
export const rpaMerchantCreate = (payload) =>
    axios.post(`${RPA}/merchants`, payload);
export const rpaMerchantGet = (id) =>
    axios.get(`${RPA}/merchants/${id}`);
export const rpaMerchantUpdate = (id, payload) =>
    axios.patch(`${RPA}/merchants/${id}`, payload);
export const rpaMerchantDelete = (id) =>
    axios.delete(`${RPA}/merchants/${id}`);

// 28.1 Credentials
export const rpaCredentialsSet = (merchantId, payload) =>
    axios.post(`${RPA}/merchants/${merchantId}/credentials`, payload);
export const rpaCredentialsClear = (merchantId) =>
    axios.delete(`${RPA}/merchants/${merchantId}/credentials`);

// 28.9 Column mappings (per-merchant, deterministic XLSX/CSV extraction)
export const rpaCanonicalFields = () =>
    axios.get(`${RPA}/canonical-fields`);
export const rpaMappingList = (merchantId) =>
    axios.get(`${RPA}/merchants/${merchantId}/mappings`);
export const rpaMappingCreate = (merchantId, payload) =>
    axios.post(`${RPA}/merchants/${merchantId}/mappings`, payload);
export const rpaMappingUpdate = (merchantId, mappingId, payload) =>
    axios.patch(`${RPA}/merchants/${merchantId}/mappings/${mappingId}`, payload);
export const rpaMappingDelete = (merchantId, mappingId) =>
    axios.delete(`${RPA}/merchants/${merchantId}/mappings/${mappingId}`);
export const rpaMappingTest = (merchantId, mappingId, payload) =>
    axios.post(`${RPA}/merchants/${merchantId}/mappings/${mappingId}/test`, payload);

// 28.2 Workflows
export const rpaWorkflowList = (params) =>
    axios.get(`${RPA}/workflows`, { params });
export const rpaWorkflowCreate = (payload) =>
    axios.post(`${RPA}/workflows`, payload);
export const rpaWorkflowGet = (id) =>
    axios.get(`${RPA}/workflows/${id}`);
export const rpaWorkflowVersions = (id) =>
    axios.get(`${RPA}/workflows/${id}/versions`);
export const rpaWorkflowDeactivate = (id) =>
    axios.post(`${RPA}/workflows/${id}/deactivate`);

// PATCH /workflows/<id>/steps — in-place update of steps_json.
// Used by the captured-steps dialog when the user deletes a step.
export const rpaWorkflowUpdateSteps = (id, steps) =>
    axios.patch(`${RPA}/workflows/${id}/steps`, { steps });

// 28.2 Schedules
export const rpaScheduleList = (workflowId) =>
    axios.get(`${RPA}/workflows/${workflowId}/schedules`);
export const rpaScheduleCreate = (workflowId, payload) =>
    axios.post(`${RPA}/workflows/${workflowId}/schedules`, payload);
export const rpaScheduleUpdate = (workflowId, scheduleId, payload) =>
    axios.patch(`${RPA}/workflows/${workflowId}/schedules/${scheduleId}`, payload);
export const rpaScheduleDelete = (workflowId, scheduleId) =>
    axios.delete(`${RPA}/workflows/${workflowId}/schedules/${scheduleId}`);

// 28.2 Recording
export const rpaRecordingStart = (payload) =>
    axios.post(`${RPA}/workflows/recording/start`, payload);
export const rpaRecordingStep = (sessionId, payload) =>
    axios.post(`${RPA}/workflows/recording/${sessionId}/step`, payload);
export const rpaRecordingSteps = (sessionId) =>
    axios.get(`${RPA}/workflows/recording/${sessionId}/steps`);
export const rpaRecordingFinalize = (sessionId, payload) =>
    axios.post(`${RPA}/workflows/recording/${sessionId}/finalize`, payload);
export const rpaRecordingDiscard = (sessionId) =>
    axios.delete(`${RPA}/workflows/recording/${sessionId}`);
export const rpaRecordingStop = (sessionId) =>
    axios.post(`${RPA}/workflows/recording/${sessionId}/stop`);
export const rpaRecordingStatus = (sessionId) =>
    axios.get(`${RPA}/workflows/recording/${sessionId}/status`);
// Frame URL (used as <img src>) — append a cachebuster on each poll.
export const rpaRecordingFrameUrl = (sessionId, cacheBust) =>
    `${RPA}/workflows/recording/${sessionId}/frame?_=${cacheBust ?? Date.now()}`;
export const rpaRecordingInput = (sessionId, payload) =>
    axios.post(`${RPA}/workflows/recording/${sessionId}/input`, payload);

// 28.3 Runs
export const rpaRunList = (params) =>
    axios.get(`${RPA}/runs`, { params });
export const rpaRunEnqueue = (payload) =>
    axios.post(`${RPA}/runs`, payload);
export const rpaRunGet = (runId) =>
    axios.get(`${RPA}/runs/${runId}`);
export const rpaRunSteps = (runId) =>
    axios.get(`${RPA}/runs/${runId}/steps`);
export const rpaRunArtifacts = (runId) =>
    axios.get(`${RPA}/runs/${runId}/artifacts`);
export const rpaRunCancel = (runId) =>
    axios.post(`${RPA}/runs/${runId}/cancel`);
// One-click re-run from the Error logs tab — creates a fresh RPARun
// row (same workflow + params) with status=QUEUED.
export const rpaRunRequeue = (runId) =>
    axios.post(`${RPA}/runs/${runId}/requeue`);
export const rpaRunRepair = (runId, payload) =>
    axios.post(`${RPA}/runs/${runId}/repair`, payload);

// 28.11 Files (raw downloaded artifacts — extracted on-demand)
export const rpaFileList = (params) =>
    axios.get(`${RPA}/files`, { params });
export const rpaFileExtract = (artifactId) =>
    axios.post(`${RPA}/files/${artifactId}/extract`);
export const rpaFileDelete = (artifactId) =>
    axios.delete(`${RPA}/files/${artifactId}`);
export const rpaFileUrl = (artifactId) =>
    axios.get(`${RPA}/artifacts/${artifactId}/url`);
// Manual upload — fallback path when IDM (or another download manager) intercepts
// the download at the OS level so the recorder/replayer can't capture it. The
// user grabs the file off disk and posts it here; the backend wraps it in a
// synthetic RPARun (kind=REPLAY, trigger=MANUAL) so the audit chain stays intact.
export const rpaFileUpload = (formData, onUploadProgress) =>
    axios.post(`${RPA}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
    });

// 28.4 Documents
export const rpaDocumentList = (params) =>
    axios.get(`${RPA}/documents`, { params });
export const rpaDocumentGet = (id) =>
    axios.get(`${RPA}/documents/${id}`);
export const rpaDocumentUpdate = (id, payload) =>
    axios.patch(`${RPA}/documents/${id}`, payload);
export const rpaDocumentReextract = (id) =>
    axios.post(`${RPA}/documents/${id}/reextract`);

// 28.4 Invoices — flattened per-line view of extracted docs.
// Each row in the response is ONE invoice. Multi-row spreadsheet
// exports (purchase reports, statement bundles) produce N rows;
// single-invoice PDFs produce one. Returns { data, total, page, per_page }.
// Params: status, merchant_id, run_id, search, from_date, to_date,
//         page, per_page.
export const rpaInvoiceList = (params) =>
    axios.get(`${RPA}/invoices`, { params });

// 28.8 Observability
export const rpaObservabilitySummary = () =>
    axios.get(`${RPA}/observability/summary`);
export const rpaObservabilityCost = (days = 30) =>
    axios.get(`${RPA}/observability/cost`, { params: { days } });
export const rpaObservabilityRunTrend = (days = 30) =>
    axios.get(`${RPA}/observability/run-trend`, { params: { days } });
export const rpaObservabilityRunCost = (runId) =>
    axios.get(`${RPA}/observability/runs/${runId}/cost`);

// 28.5 Reconciliation
export const rpaReconciliationMatches = (params) =>
    axios.get(`${RPA}/reconciliation/matches`, { params });
// Unmatched-docs endpoint now accepts: page, per_page, q, min_amount,
// max_amount, from_date, to_date, merchant_id. Caller passes them
// through unchanged; backend returns { data, total, page, per_page }.
export const rpaReconciliationUnmatchedDocs = (params) =>
    axios.get(`${RPA}/reconciliation/unmatched-documents`, { params });
// Unmatched-txns endpoint: page, per_page, q, days, from_date, to_date,
// min_amount, max_amount, stripe_account_id.
export const rpaReconciliationUnmatchedTxns = (params) =>
    axios.get(`${RPA}/reconciliation/unmatched-transactions`, { params });
export const rpaReconciliationAutoMatch = (payload) =>
    axios.post(`${RPA}/reconciliation/auto-match`, payload);
export const rpaReconciliationSuggest = (documentId, params) =>
    axios.get(`${RPA}/reconciliation/suggest/${documentId}`, { params });
export const rpaReconciliationManualLink = (payload) =>
    axios.post(`${RPA}/reconciliation/manual-link`, payload);
export const rpaReconciliationUnlink = (matchId) =>
    axios.post(`${RPA}/reconciliation/unlink/${matchId}`);

// Approve / un-approve a single auto-match. Stamps reviewed_at + by;
// pass { undo: true } to clear it. Used by the per-row Approve button
// in the "Need review" tab.
export const rpaReconciliationReviewMatch = (matchId, undo = false) =>
    axios.post(`${RPA}/reconciliation/matches/${matchId}/review`,
               undo ? { undo: true } : {});

// Bulk approve. Used by the "Need review" tab's row-checkbox bulk
// action. Payload: { match_ids: [...], undo?: bool }.
export const rpaReconciliationReviewBulk = (matchIds, undo = false) =>
    axios.post(`${RPA}/reconciliation/matches/review-bulk`,
               { match_ids: matchIds, undo: !!undo });

// Reconcile redesign needs merchant list for the merchant→bank cascade.
// Existing endpoint, no new helper needed beyond this one re-exported
// alias for clarity.
export const rpaListMerchants = (params) =>
    axios.get(`${RPA}/merchants`, { params });

// Active Stripe Financial Connections accounts — used by the Reconcile
// tab to render the "which bank does this transaction belong to" strip.
// The endpoint already filters to ACTIVE connections; pass selected_only=true
// to limit to accounts the user explicitly opted into for syncing.
export const rpaListStripeAccounts = (params) =>
    axios.get(`/api/finance/stripe/accounts`, { params });

// 28.4 Date-filter recipe presets — used by the per-step picker in
// the recorder / step editor. Each workflow step can carry an optional
// `date_filter` recipe; the runtime resolves it at execute time so the
// scheduled bot types fresh dates instead of stale recorded literals.
export const rpaDateFilterPresets = () =>
    axios.get(`${RPA}/date-filter/presets`);
// Preview supports two shapes:
//   { date_filters: { start_date: {preset:..}, end_date: {preset:..} } }
//   { date_filters: { preset: 'yesterday', format: 'YYYY-MM-DD' } }
// The second form returns the resolved value under start_date for
// convenience — the server treats a single side as start_date.
export const rpaDateFilterPreview = (date_filters) =>
    axios.post(`${RPA}/date-filter/preview`, { date_filters });

// ---- Summaries ----
export const transactionIntelligenceSummary = () =>
    axios.get(`${BASE}/transaction-intelligence/summary`);
export const financialOperationsSummary = () =>
    axios.get(`${BASE}/financial-operations/summary`);

// =======================================================================
// Transaction Intelligence (Agentic Transaction Intelligence Layer)
// =======================================================================

// Categorization (9.1) — use_ai enables Gemini reasoning on low-confidence
export const catResolve = (transaction_id, use_ai = true) =>
    axios.post(`${BASE}/categorization/resolve`, { transaction_id, use_ai });
export const catHistory = (params) =>
    axios.get(`${BASE}/categorization/history`, { params });
export const catVendor = (merchant_name) =>
    axios.get(`${BASE}/categorization/vendor`, { params: { merchant_name } });
export const catSplit = (transaction_id, use_ai = true) =>
    axios.post(`${BASE}/categorization/detect-split`, { transaction_id, use_ai });
export const catTransfer = (transaction_id) =>
    axios.post(`${BASE}/categorization/identify-transfer`, { transaction_id });
export const catBatch = (batch_size = 50, use_ai = true) =>
    axios.post(`${BASE}/categorization/batch`, { batch_size, use_ai });

// Reconciliation (9.2)
export const reconMatch = (bank_txn_id) =>
    axios.post(`${BASE}/reconciliation/match`, { bank_txn_id });
export const reconOneToMany = (bank_txn_id) =>
    axios.post(`${BASE}/reconciliation/one-to-many`, { bank_txn_id });
export const reconManyToOne = (journal_line_id) =>
    axios.post(`${BASE}/reconciliation/many-to-one`, { journal_line_id });
export const reconTiming = () =>
    axios.get(`${BASE}/reconciliation/timing-differences`);
export const reconMissing = (min_age_days = 5) =>
    axios.get(`${BASE}/reconciliation/missing-entries`, { params: { min_age_days } });
export const reconReport = (period_start, period_end) =>
    axios.get(`${BASE}/reconciliation/report`, { params: { period_start, period_end } });

// Anomaly (9.3) — use_ai enables AI contextual assessment
export const anomalyBaseline = (params) =>
    axios.get(`${BASE}/anomaly/baseline`, { params });
export const anomalyAmount = (transaction_id, use_ai = true) =>
    axios.post(`${BASE}/anomaly/amount`, { transaction_id, use_ai });
export const anomalyVelocity = (window_hours = 24) =>
    axios.get(`${BASE}/anomaly/velocity`, { params: { window_hours } });
export const anomalyNewCounterparty = (recent_days = 7) =>
    axios.get(`${BASE}/anomaly/new-counterparty`, { params: { recent_days } });
export const anomalyDuplicates = (days = 7) =>
    axios.get(`${BASE}/anomaly/duplicates`, { params: { days } });
export const anomalyWeekend = (recent_days = 30) =>
    axios.get(`${BASE}/anomaly/weekend`, { params: { recent_days } });
export const anomalyCompositeScan = (use_ai = true) =>
    axios.post(`${BASE}/anomaly/composite-scan`, { use_ai });

// Data Quality (9.4)
export const dqOrphans = () => axios.get(`${BASE}/data-quality/orphans`);
export const dqClassification = () => axios.get(`${BASE}/data-quality/classification`);
export const dqMandatory = () => axios.get(`${BASE}/data-quality/mandatory-fields`);
export const dqStale = () => axios.get(`${BASE}/data-quality/stale`);
export const dqBalance = () => axios.get(`${BASE}/data-quality/balance-integrity`);
export const dqCoa = () => axios.get(`${BASE}/data-quality/coa-hygiene`);

// =======================================================================
// Financial Operations (Agentic Financial Operations Agents)
// =======================================================================

// Cash Flow (10.1) — use_ai enables AI narrative generation
export const cfForecast = (min_balance = 0) =>
    axios.get(`${BASE}/cashflow/forecast`, { params: { min_balance } });
export const cfArPredict = () => axios.get(`${BASE}/cashflow/ar-predict`);
export const cfApSchedule = (horizon_days = 60) =>
    axios.get(`${BASE}/cashflow/ap-schedule`, { params: { horizon_days } });
export const cfScenarios = (horizon_days = 30) =>
    axios.get(`${BASE}/cashflow/scenarios`, { params: { horizon_days } });
export const cfAlerts = (min_balance = 10000) =>
    axios.get(`${BASE}/cashflow/alerts`, { params: { min_balance } });
export const cfNarrative = (period_days = 7, use_ai = true) =>
    axios.post(`${BASE}/cashflow/narrative`, { period_days, use_ai });

// Budget (10.2) — use_ai enables AI root cause analysis
export const budVariance = () => axios.get(`${BASE}/budget/variance`);
export const budRootCause = (account_id, use_ai = true) =>
    axios.post(`${BASE}/budget/root-cause`, { account_id, use_ai });
export const budYearEnd = () => axios.get(`${BASE}/budget/year-end`);
export const budReallocate = () => axios.get(`${BASE}/budget/reallocate`);
export const budScorecards = () => axios.get(`${BASE}/budget/scorecards`);
export const budReport = () => axios.get(`${BASE}/budget/report`);

// Vendor Spend (10.3)
export const vsConcentration = (lookback_days = 365) =>
    axios.get(`${BASE}/vendor-spend/concentration`, { params: { lookback_days } });
export const vsTerms = () => axios.get(`${BASE}/vendor-spend/terms`);
export const vsDuplicates = () => axios.get(`${BASE}/vendor-spend/duplicates`);
export const vsTrend = (months = 12) =>
    axios.get(`${BASE}/vendor-spend/trend`, { params: { months } });
export const vsBenchmark = () => axios.get(`${BASE}/vendor-spend/benchmark`);
export const vsPerformance = () => axios.get(`${BASE}/vendor-spend/performance`);

// Working Capital (10.4) — use_ai enables AI executive narrative
export const wcCcc = (lookback_days = 365) =>
    axios.get(`${BASE}/working-capital/ccc`, { params: { lookback_days } });
export const wcDso = () => axios.get(`${BASE}/working-capital/dso`);
export const wcDpo = () => axios.get(`${BASE}/working-capital/dpo`);
export const wcInventory = () => axios.get(`${BASE}/working-capital/inventory`);
export const wcForecast = (horizon_days = 90) =>
    axios.get(`${BASE}/working-capital/forecast`, { params: { horizon_days } });
export const wcExecutive = (use_ai = true) =>
    axios.post(`${BASE}/working-capital/executive`, { use_ai });

// =======================================================================
// Agentic Learning & Continuous Improvement
// =======================================================================

// Feedback Learning (15.1)
export const flIngest = (correction_type, original_value, corrected_value, run_id) =>
    axios.post(`${BASE}/feedback-learning/ingest`, { correction_type, original_value, corrected_value, run_id });
export const flCorrectionPatterns = (lookback_days = 90, use_ai = true) =>
    axios.post(`${BASE}/feedback-learning/correction-patterns`, { lookback_days, use_ai });
export const flFewShot = (agent_type = 'categorization', limit = 10) =>
    axios.post(`${BASE}/feedback-learning/few-shot`, { agent_type, limit, use_ai: true });
export const flAbPrompt = (agent_definition_id) =>
    axios.get(`${BASE}/feedback-learning/ab-prompt`, { params: { agent_definition_id } });
export const flKnowledgeUpdate = (category, key, value, source = 'user') =>
    axios.post(`${BASE}/feedback-learning/knowledge-update`, { category, key, value, source });
export const flLearningVelocity = (lookback_days = 90) =>
    axios.get(`${BASE}/feedback-learning/learning-velocity`, { params: { lookback_days } });

// Agent Performance (15.2)
export const apAccuracy = (lookback_days = 30) =>
    axios.get(`${BASE}/agent-performance/accuracy`, { params: { lookback_days } });
export const apConfidence = (agent_type) =>
    axios.get(`${BASE}/agent-performance/confidence`, { params: { agent_type } });
export const apOverrideRate = (lookback_days = 30) =>
    axios.get(`${BASE}/agent-performance/override-rate`, { params: { lookback_days } });
export const apCostPerOp = (lookback_days = 30) =>
    axios.get(`${BASE}/agent-performance/cost-per-op`, { params: { lookback_days } });
export const apThroughput = () =>
    axios.get(`${BASE}/agent-performance/throughput`);
export const apBenchmarker = (use_ai = true) =>
    axios.post(`${BASE}/agent-performance/benchmarker`, { use_ai });

// Continuous Improvement (15.3)
export const ciAutoRules = (min_corrections = 5, use_ai = true) =>
    axios.post(`${BASE}/continuous-improvement/auto-rules`, { min_corrections, use_ai });
export const ciThresholdTune = () =>
    axios.get(`${BASE}/continuous-improvement/threshold-tune`);
export const ciPromptVersions = (agent_definition_id) =>
    axios.get(`${BASE}/continuous-improvement/prompt-versions`, { params: { agent_definition_id } });
export const ciKbCurator = () =>
    axios.get(`${BASE}/continuous-improvement/kb-curator`);
export const ciSelfImproveReport = (use_ai = true) =>
    axios.post(`${BASE}/continuous-improvement/self-improve-report`, { use_ai });
export const ciDeadLetters = (lookback_days = 30) =>
    axios.get(`${BASE}/continuous-improvement/dead-letters`, { params: { lookback_days } });

// =======================================================================
// Agentic Autonomous Workflow Engine
// =======================================================================

// Summaries
export const autonomousWorkflowSummary = () =>
    axios.get(`${BASE}/autonomous-workflow/summary`);

// 11.1 Month-End Close
export const mecDag = (period_id) =>
    axios.post(`${BASE}/month-end-close/dag`, { period_id });
export const mecAccruals = (period_id, use_ai = true) =>
    axios.post(`${BASE}/month-end-close/accruals`, { period_id, use_ai });
export const mecIntercompany = (period_id) =>
    axios.get(`${BASE}/month-end-close/intercompany`, { params: { period_id } });
export const mecDepreciation = (as_of_date) =>
    axios.post(`${BASE}/month-end-close/depreciation`, { as_of_date });
export const mecRevRec = (period_id) =>
    axios.post(`${BASE}/month-end-close/rev-rec`, { period_id });
export const mecStatus = (period_id, use_ai = true) =>
    axios.post(`${BASE}/month-end-close/status`, { period_id, use_ai });

// 11.2 AP Automation
export const apZeroTouch = (batch_size = 20) =>
    axios.post(`${BASE}/ap-automation/zero-touch`, { batch_size });
export const apPaymentOpt = (horizon_days = 30) =>
    axios.get(`${BASE}/ap-automation/payment-optimization`, { params: { horizon_days } });
export const apVendorComm = (vendor_id, comm_type = 'payment_status', use_ai = true) =>
    axios.post(`${BASE}/ap-automation/vendor-communication`, { vendor_id, comm_type, use_ai });
export const apThreeWay = (bill_id) =>
    axios.post(`${BASE}/ap-automation/three-way-match`, { bill_id });
export const apRecurring = () =>
    axios.get(`${BASE}/ap-automation/recurring-patterns`);
export const apAgingAlerts = (alert_days = 30, use_ai = true) =>
    axios.post(`${BASE}/ap-automation/aging-alerts`, { use_ai });

// 11.3 AR Automation
export const arCollection = () =>
    axios.get(`${BASE}/ar-automation/collection-priority`);
export const arCashApp = (payment_id) =>
    axios.post(`${BASE}/ar-automation/cash-application`, { payment_id });
export const arDunning = (customer_id, use_ai = true) =>
    axios.post(`${BASE}/ar-automation/dunning`, { customer_id, use_ai });
export const arInvoiceValidate = (invoice_id) =>
    axios.post(`${BASE}/ar-automation/invoice-validate`, { invoice_id });
export const arRevenueForecast = (horizon_days = 90) =>
    axios.get(`${BASE}/ar-automation/revenue-forecast`, { params: { horizon_days } });
export const arCreditScore = (customer_id) =>
    axios.get(`${BASE}/ar-automation/credit-score`, { params: { customer_id } });

// 11.4 Payroll Automation
export const payrollPreVal = (run_id) =>
    axios.post(`${BASE}/payroll-automation/pre-validation`, { run_id });
export const payrollAnomaly = (run_id) =>
    axios.post(`${BASE}/payroll-automation/anomaly-detection`, { run_id });
export const payrollTaxVerify = (run_id) =>
    axios.post(`${BASE}/payroll-automation/tax-verification`, { run_id });
export const payrollPostRecon = (run_id) =>
    axios.post(`${BASE}/payroll-automation/post-reconciliation`, { run_id });
export const payrollComplianceCal = () =>
    axios.get(`${BASE}/payroll-automation/compliance-calendar`);
export const payrollChangeMon = (lookback_days = 30) =>
    axios.get(`${BASE}/payroll-automation/employee-changes`, { params: { lookback_days } });

// =======================================================================
// Natural Language Command Center
// =======================================================================

export const nlpTransactionEntry = (command) =>
    axios.post(`${BASE}/nlp-command/transaction-entry`, { command });
export const nlpBillEntry = (command) =>
    axios.post(`${BASE}/nlp-command/bill-entry`, { command });
export const nlpCorrection = (command) =>
    axios.post(`${BASE}/nlp-command/correction`, { command });
export const nlpBatchCommand = (commands) =>
    axios.post(`${BASE}/nlp-command/batch-command`, { commands });
export const nlpDisambiguate = (command, context) =>
    axios.post(`${BASE}/nlp-command/disambiguate`, { command, context });
export const nlpFinancialQuery = (query) =>
    axios.post(`${BASE}/nlp-command/financial-query`, { query });
export const nlpComparativeAnalysis = (query) =>
    axios.post(`${BASE}/nlp-command/comparative-analysis`, { query });
export const nlpDrillDown = (query, prior_context) =>
    axios.post(`${BASE}/nlp-command/drill-down`, { query, prior_context });
export const nlpRatioQuery = (query) =>
    axios.post(`${BASE}/nlp-command/ratio-query`, { query });
export const nlpCashPosition = () =>
    axios.get(`${BASE}/nlp-command/cash-position`);
export const nlpWhatIf = (scenario) =>
    axios.post(`${BASE}/nlp-command/what-if`, { scenario });
export const nlpReportBuilder = (description) =>
    axios.post(`${BASE}/nlp-command/report-builder`, { description });
export const nlpScheduledReport = (description) =>
    axios.post(`${BASE}/nlp-command/scheduled-report`, { description });
export const nlpBoardDeck = (instructions) =>
    axios.post(`${BASE}/nlp-command/board-deck`, { instructions });
export const nlpAuditPackage = (description) =>
    axios.post(`${BASE}/nlp-command/audit-package`, { description });
export const nlpAdhocAnalysis = (query) =>
    axios.post(`${BASE}/nlp-command/adhoc-analysis`, { query });
export const nlpExport = (description) =>
    axios.post(`${BASE}/nlp-command/export`, { description });
export const nlpCommandSummary = () =>
    axios.get(`${BASE}/nlp-command/summary`);

// =======================================================================
// Document Intelligence Pipeline
// =======================================================================

// 13.1 Invoice Understanding
export const docParseInvoice = (document_text, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/invoice/parse`, { document_text, use_ai });
export const docTemplateLearn = (vendor_id) =>
    axios.post(`${BASE}/document-intelligence/invoice/template`, { vendor_id });
export const docExtractLines = (document_text, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/invoice/extract-lines`, { document_text, use_ai });
export const docCrossReference = (extracted_data) =>
    axios.post(`${BASE}/document-intelligence/invoice/cross-reference`, { extracted_data });
export const docDetectLanguage = (document_text) =>
    axios.post(`${BASE}/document-intelligence/invoice/detect-language`, { document_text });
export const docHandwritten = (document_text, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/invoice/handwritten`, { document_text, use_ai });

// 13.2 Contract Analysis
export const docContractTerms = (contract_text, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/contract/extract-terms`, { contract_text, use_ai });
export const docObligationCalendar = () =>
    axios.get(`${BASE}/document-intelligence/contract/obligation-calendar`);
export const docContractCompare = (contract_a, contract_b, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/contract/compare`, { contract_a, contract_b, use_ai });
export const docRiskClauses = (contract_text, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/contract/risk-clauses`, { contract_text, use_ai });
export const docRevRecMapper = (contract_text, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/contract/rev-rec-mapper`, { contract_text, use_ai });
export const docContractSearch = (keyword) =>
    axios.get(`${BASE}/document-intelligence/contract/search`, { params: { keyword } });

// 13.3 Bank Statement
export const docParseStatement = (statement_text, bank_name) =>
    axios.post(`${BASE}/document-intelligence/statement/parse`, { statement_text, bank_name });
export const docReconcileStatement = (statement_transactions, period_start, period_end) =>
    axios.post(`${BASE}/document-intelligence/statement/reconcile`, { statement_transactions, period_start, period_end });
export const docStatementTrends = (statements_data, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/statement/trends`, { statements_data, use_ai });
export const docBankFees = (statement_transactions, use_ai = true) =>
    axios.post(`${BASE}/document-intelligence/statement/fees`, { statement_transactions, use_ai });
export const docConsolidateAccounts = (account_statements) =>
    axios.post(`${BASE}/document-intelligence/statement/consolidate`, { account_statements });
export const docArchiveStatement = (statement_data, account_id, period) =>
    axios.post(`${BASE}/document-intelligence/statement/archive`, { statement_data, account_id, period });

// Document Intelligence Summary
export const docIntelligenceSummary = () =>
    axios.get(`${BASE}/document-intelligence/summary`);

// =======================================================================
// Compliance, Fraud & Risk
// =======================================================================

// Summary
export const complianceFraudRiskSummary = () =>
    axios.get(`${BASE}/compliance-fraud-risk/summary`);

// 14.1 Fraud Detection
export const fraudGhostVendors = () =>
    axios.get(`${BASE}/fraud-detection/ghost-vendors`);
export const fraudSplitTransactions = (threshold = 5000) =>
    axios.post(`${BASE}/fraud-detection/split-transactions`, { threshold });
export const fraudExpensePatterns = (lookback_days = 90) =>
    axios.get(`${BASE}/fraud-detection/expense-patterns`, { params: { lookback_days } });
export const fraudPaymentDiversion = () =>
    axios.get(`${BASE}/fraud-detection/payment-diversion`);
export const fraudJournalEntry = (use_ai = true) =>
    axios.post(`${BASE}/fraud-detection/journal-entry-fraud`, { use_ai });
export const fraudBenfordsLaw = (data_source = 'bills') =>
    axios.post(`${BASE}/fraud-detection/benfords-law`, { data_source });
export const fraudCompositeScan = (use_ai = true) =>
    axios.post(`${BASE}/fraud-detection/composite-scan`, { use_ai });

// 14.2 Internal Controls
export const controlsSegregation = () =>
    axios.get(`${BASE}/internal-controls/segregation-of-duties`);
export const controlsAccessReview = () =>
    axios.get(`${BASE}/internal-controls/access-review`);
export const controlsCloseChecker = (period_id) =>
    axios.post(`${BASE}/internal-controls/close-controls`, { period_id });
export const controlsPolicyViolations = (lookback_days = 30) =>
    axios.get(`${BASE}/internal-controls/policy-violations`, { params: { lookback_days } });
export const controlsEffectiveness = (control_type = 'bill_approval', sample_size = 25) =>
    axios.post(`${BASE}/internal-controls/effectiveness-test`, { control_type, sample_size });
export const controlsAuditEvidence = (period_start, period_end) =>
    axios.post(`${BASE}/internal-controls/audit-evidence`, { period_start, period_end });

// 14.3 Regulatory Monitor
export const regTaxLawChanges = (use_ai = true) =>
    axios.post(`${BASE}/regulatory-monitor/tax-law-changes`, { use_ai });
export const regAccountingStandards = (use_ai = true) =>
    axios.post(`${BASE}/regulatory-monitor/accounting-standards`, { use_ai });
export const regLaborRegulations = (use_ai = true) =>
    axios.post(`${BASE}/regulatory-monitor/labor-regulations`, { use_ai });
export const regFilingDeadlines = () =>
    axios.get(`${BASE}/regulatory-monitor/filing-deadlines`);
export const regComplianceGaps = (use_ai = true) =>
    axios.post(`${BASE}/regulatory-monitor/compliance-gaps`, { use_ai });
export const regImpactAssessment = (regulation_text, use_ai = true) =>
    axios.post(`${BASE}/regulatory-monitor/impact-assessment`, { regulation_text, use_ai });
